<template>
  <div>
    <Breadcrumb
      :id="chosenMapObjectType.id"
      :name="chosenMapObjectType.name"
      :type="$t('drawing.multiselect')"
      :showBackButton="true"
      @backButtonPressed="goBack"
    >
      <template v-slot:icon>
        <MapIcon v-bind="networkIcon" icon="13_roadsign" :size="39"/>
      </template>
    </Breadcrumb>
    <CollapsePanel
      :title="$t('selections.map_object_info')"
      :loading="false"
      :showCount=false
      :showCollapseAction="false"
      :collapsed="true"
      icon="pen"
    >
      <div class="create-selection-content">
        <span v-if="selectedObjects.length === 0">
          <small><i>{{ $t('drawing.no_objects_selected') }}</i></small>
        </span>
        <span v-for="(mapObject, index) in selectedObjects"
              :key="mapObject.id+'-'+index">
          <IconByTypeId :map_object_type_id="mapObject.map_object_type_id"
                        :label="mapObject | mapObjectLabel"
                        :size="35"
          />
        </span>
      </div>
    </CollapsePanel>

    <CollapsePanel
      :title="$t('route_management.object_information')"
      :loading=loading
      :showCount=false
      :collapsed="true"
      :showCollapseAction="false"
    >
      <div class="create-object-content">
        <div class="field" style="margin-bottom: 40px !important;">
          <label for="name">{{ $t('model.name') | capitalize }}:</label>
          <input
            id="name"
            name="name"
            type="text"
            v-model="objectData.name"
            :class="{'error': errors.name !== null}"
            @change="resetError('name')"
            autofocus
          />
          <span class="input-error">
            {{ errors.name }}
          </span>
        </div>
        <div class="field" style="margin-bottom: 5px !important;">
          <label for="map_object_status_id" class="status-label">
            {{ $t('model.status') | capitalize }}:
          </label>
          <div class="field content status-select">
            <MultiSelect
              id="map_object_status_id"
              :options="statusValues"
              v-model="mapObjectStatus"
              track-by="id"
              label="name"
              :multiple="false"
              :close-on-select="true"
              :allow-empty="false"
              :preselect-first="true"
              placeholder=""
              selectLabel=""
              selectedLabel=""
              deselectLabel=""
              @input="updateStatus"
            />
          </div>
        </div>
        <div v-if="mapObjectLock && mapObjectLock.value" class="field" style="margin-top: -10px;">
          <label for="created_by" class="created_by-label">
            {{ $t('model.owner') | capitalize }}:
          </label>
          <div class="field content created_by-select">
            <MultiSelect
              id="created_by"
              :options="createdByValues"
              v-model="createdBy"
              track-by="id"
              label="name"
              :multiple="false"
              :close-on-select="true"
              :allow-empty="false"
              :preselect-first="true"
              placeholder=""
              selectLabel=""
              selectedLabel=""
              deselectLabel=""
              @input="updateCreatedBy"
            />
          </div>
        </div>
      </div>
    </CollapsePanel>
    <CollapsePanel
      :title="$t('selections.attribute_info')"
      :loading="attributes === null"
      :showCount=false
      :showCollapseAction="false"
      :collapsed="true"
    >
      <div class="create-object-content">
        <div v-for="(attribute, idx) in attributes"
             :key="idx"
             class="attribute-field"
        >
          <span class="title">
              <span v-if="attribute.required" class="required">*</span>
              {{ attribute.name }}:
          </span>
          <div class="field content">
            <AttributeInput
              :disabled="false"
              :attribute="attribute"
              :error="errors[attribute.id]"
              :setDefault="false"
            />
          </div>
        </div>
      </div>
    </CollapsePanel>
    <div class="create-object-buttons">
      <a class="btn grey"
         @click="goBack"
      >
        {{ $t('button.back') | capitalize }}
      </a>
      <a class="btn module-color-background save-button"
         @click="saveObjects"
      >
        {{ $t(`button.edit`) | capitalize }}
      </a>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MultiSelect from 'vue-multiselect';

import MapObjectApi from '@/api/map_object';
import MapObjectTypeApi from '@/api/map_object_type';

import EventBus from '@/events/event-bus';

import MapIcon from '@/components/Map/MapIcon.vue';
import Breadcrumb from '@/components/Layout/Breadcrumb.vue';
import NetworkIcon from '@/enums/NetworkIcon';
import AttributeInput from '@/components/AttributeInput.vue';
import CollapsePanel from '@/components/CollapsePanel.vue';
import IconByTypeId from '@/components/IconByTypeId.vue';

export default {
  name: 'drawing.multiselect.create',
  components: {
    IconByTypeId,
    CollapsePanel,
    AttributeInput,
    Breadcrumb,
    MapIcon,
    MultiSelect,
  },
  computed: {
    ...mapGetters({
      keyValues: 'settings/getKeyValues',
      network: 'settings/getNetworkModel',
      mapObjectStatuses: 'settings/getMapObjectStatuses',
      mapObjectTypes: 'settings/getMapObjectTypes',
      mapObjectTypeMapObjectStatuses: 'settings/getMapObjectTypeMapObjectStatuses',
      selectedObjects: 'map/getSelected',
      users: 'settings/getUsers',
    }),
    chosenMapObjectType() {
      if (this.$route.params.type !== null && this.$route.params.type !== undefined) {
        const selectedType = parseInt(this.$route.params.type, 10);
        return this.mapObjectTypes[selectedType];
      }
      return {};
    },
    statusesForType() {
      const currentType = this.chosenMapObjectType.id;
      if (!currentType) {
        return [];
      }
      if (!this.mapObjectTypeMapObjectStatuses || this.mapObjectTypeMapObjectStatuses.length === 0) {
        return [];
      }
      const statusesForType = this.mapObjectTypeMapObjectStatuses
        .filter(item => item.map_object_type_id === currentType)
        .map(item => item.map_object_status_id);
      return Object.values(this.mapObjectStatuses).filter(item => statusesForType.includes(item.id));
    },
    statusValues() {
      const statuses = Object.values(this.statusesForType);
      statuses.unshift({
        id: 0,
        name: this.$t('drawing.unchanged'),
      });
      return statuses;
    },
    createdByValues() {
      const users = JSON.parse(JSON.stringify(Object.values(this.users)));

      if (this.mapObjectLock && this.mapObjectLock.value && this.mapObjectLock.value === 'team') {
        users.map((user) => {
          if (user.team && user.team.data && user.team.data.length > 0) {
            const newUser = user;
            const teams = user.team.data.map(item => item.name).join(', ');
            newUser.name = `${user.name} (${teams})`;
            return newUser;
          }
          return user;
        });
      }
      users.unshift({
        id: 0,
        name: this.$t('drawing.unchanged'),
      });
      return users;
    },
    mapObjectLock() {
      if (!this.network || !this.network.id) {
        return null;
      }
      const lockItems = Object.values(this.keyValues)
        .filter(item => item.key.includes(`lock_mapobject_${this.network.id}`));
      if (lockItems.length > 0) {
        return lockItems[0];
      }
      return null;
    },
  },
  data() {
    return {
      loading: false,
      networkIcon: NetworkIcon,
      objectData: {
        name: '',
        map_object_status_id: 0,
        created_by: 0,
        map_object_type_id: 0,
      },
      attributes: null,
      errors: {
        name: null,
        created_by: null,
        mapObjects: null,
      },
      mapObjectStatus: null,
      createdBy: null,
      showCollapseMenu: false,
    };
  },
  methods: {
    ...mapActions({
      fetchSelection: 'selected/fetchSelection',
      removeSelectedId: 'map/removeSelected',
      resetSelected: 'map/resetSelected',
      resetMap: 'map/resetMap',
      updateMapObject: 'settings/updateMapObject',
      getMapObjectById: 'settings/getMapObjectById',
    }),
    goBack() {
      this.$router.push({ name: 'drawing.multiselect.choose', params: { type: this.$route.params.type } });
    },
    resetError(field) {
      this.errors[field] = null;
    },
    loadAttributes() {
      MapObjectTypeApi.getMapObjectTypeAttributes(this.chosenMapObjectType.id, 'attributeLookupValue').then((response) => {
        this.attributes = response.data;
      });
    },
    updateStatus(value) {
      if (!value) {
        this.objectData.map_object_status_id = null;
      } else {
        this.objectData.map_object_status_id = value.id;
      }
    },
    updateCreatedBy(value) {
      if (!value) {
        this.objectData.created_by = 0;
      } else {
        this.objectData.created_by = value.id;
      }
    },
    toggleShowCollapseMenu() {
      this.showCollapseMenu = !this.showCollapseMenu;
    },
    deleteMapObject(mapObject, emit = true) {
      console.log('deleteMapObject', mapObject, emit);
      this.removeSelectedId({ mapObjectId: mapObject.id });
    },
    emptySelectionObjects() {
      this.resetSelected();
    },
    removeMapObjectFromSelectionList(mapObject) {
      this.deleteMapObject(mapObject, false);
    },
    filterEmptyMultiLookupValues(data) {
      return data.map((attribute) => {
        if (attribute.type === 'multi_lookup' && attribute.value && attribute.value.length === 0) {
          const newAttribute = attribute;
          delete newAttribute.value;
          return newAttribute;
        }
        return attribute;
      });
    },
    saveObjects() {
      this.loading = true;
      const data = {
        update: this.objectData,
        ids: this.selectedObjects.map(a => a.id),
      };
      if (data.update.name === '') {
        delete data.update.name;
      }
      if (data.update.created_by === '' || data.update.created_by === 0) {
        delete data.update.created_by;
      }
      if (data.update.map_object_status_id === 0) {
        delete data.update.map_object_status_id;
      }
      data.update.map_object_type_id = this.chosenMapObjectType.id;
      data.update.attribute = {
        data: this.filterEmptyMultiLookupValues(
          JSON.parse(JSON.stringify(this.attributes)),
        ),
      };
      MapObjectApi.updateMapObjectsInBulk(data)
        .then(() => {
          this.$notify({
            type: 'success',
            title: this.$t('dialog.changes_saved'),
          });
          this.updateLocalObjects(data);
          this.finishUp();
        }).catch((error) => {
          console.log('error', error);
          this.$notify({
            type: 'error',
            title: this.$t('error.update_failed', { model: this.$tc('models.mapobject', 0) }),
            data: error.response,
          });
          this.loading = false;
        });
    },
    async updateLocalObjects(data) {
      await Promise.all(data.ids.map(async (id) => {
        const mapObject = await this.getMapObjectById({ mapObjectId: id });
        if (!mapObject) return;

        if (data.update.name) {
          mapObject.name = data.update.name;
        }

        if (data.update.attribute && data.update.attribute.data) {
          const labelAttributes = data.update.attribute.data
            .filter(item => item.id === this.chosenMapObjectType.label_attribute_id);
          if (labelAttributes.length > 0) {
            mapObject.label = labelAttributes[0].value || mapObject.label;
          }
        }

        if (data.update.map_object_status_id) {
          mapObject.map_object_status_id = data.update.map_object_status_id;
          delete mapObject.mapObjectStatus;
        }

        await this.updateMapObject({ mapObject, redraw: false });
      }));
    },
    finishUp() {
      this.emptySelectionObjects();
      this.resetMap();
      this.objectData = {
        name: '',
        map_object_status_id: 0,
        created_by: 0,
        map_object_type_id: 0,
      };
      this.attributes = null;
      this.loading = false;
      this.$router.push({ name: 'drawing.multiselect' });
    },
  },
  mounted() {
    console.log('mounted selected', this.selectedObjects);
    if (this.selectedObjects.length === 0) {
      this.$router.push({ name: 'drawing.multiselect' });
    }
    this.loadAttributes();
    EventBus.$emit('Map.disableInteraction');
    EventBus.$emit('Filter.disable');
  },
  beforeDestroy() {
    EventBus.$emit('Map.enableInteraction');
    EventBus.$emit('Filter.enable');
  },
};
</script>

<style scoped>
.field {
  overflow: visible;
  min-height: 40px;
}
.field label {
  width: auto;
}
.create-object-content {
  padding: 10px 20px 0 20px;
  overflow: inherit;
}
.status-label, .created_by-label {
  display: inline-flex;
  float: initial;
}
.status-select, .created_by-select {
  display: inline-flex;
  float: initial;
  overflow: inherit;
}
.attribute-field {
  overflow: inherit;
}

.create-object-buttons {
  padding: 10px 20px;
  position: relative;
  background: #f2f7fe;
  min-height: 50px;
  border-bottom: 1px solid #d9dadb;
}

.create-object-buttons .save-button {
  float: right;
  margin-right: 7px;
}
.field span#parent_id {
  font-weight: 300;
  font-size: 1em;
  float: right;
  line-height: 36px;
}
.input-error {
  float: left;
  width: 100%;
  color: #ff7676;
}
input.error {
  border-color: #ff7676;
}


.delete-image {
  float: left;
  font-weight: bold;
  color: white;
  background-color: #ef325e;
  border-radius: 50%;
  height: 19px;
  width: 19px;
  position: absolute;
  margin-left: -17px;
  cursor: pointer;
  font-size: 11px;
  text-align: center;
  line-height: 19px;
}

.delete-image:hover {
  background-color: #a82540;
}

.objects-error {
  width: 100%;
  color: #ff7676;
}

.create-selection-content {
  padding: 10px 20px;
  overflow: inherit;
}

.create-selection-content .field label {
  width: 35%;
  display: inline-flex;
}
</style>
